/**
 * Local stand-in for the real backend's `build_block` incremental trial-delivery
 * behavior, used only for local RL/adaptive testing (`npm run dev:<N>:rl:browser`)
 * - see `shouldUseMockBackend()` in `adaptiveTrialsService.js`. Same request/response
 * shape as the real `/fellow` endpoint, served from a per-mission trial pool below
 * (dispatched by `payload.missionNumber`, since each mission needs different fields).
 */

// request - 2 is a placeholder guess, easy to tune once that's known.
const BATCH_SIZE = 2;
// Forced artificial delay so the loading indicator is actually visible/testable.
const MOCK_NETWORK_DELAY_MS = 2000;

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Real trials from a mission 0 adaptive task file (gs01_mission_0_task_09_vGF04_M0_AdaptiveRL.json).
// trialNumber 1-3 are already used as the static trials in mission0_adaptive.json, so the mock
// serves from 4 onward.
const MISSION_0_TRIAL_POOL = [
  {
    CSI: 100,
    correctKeycode: 1,
    errorT1keycode: 7,
    rewardPoints: 1,
    trialNumber: 1,
    block: 1,
    releaseMechanism: 1,
    timeout_ms: 3400,
    correctKeychar: "s",
    trialType: "TASK",
    stimulusFile: "https://backend2.bgbehavior.com/gs01_license_images/gs01_120px_U_10_6_10_450_set_0003.png",
    cueFile: "target_precue_brightness.svg",
    packageFile: "package_low.svg",
    package_appearance: 150,
    laneMapping: 0,
    agentFile: "footer_LaneLabels.svg",
    validation: {
      letterCode: 18,
      numberCode: 27,
      letterBrightness: 10,
      numberBrightness: 10,
      backgroundBrightness: 450,
      blockType: 1,
      taskSwitch: 0,
      letterOrder: 1,
      cueShape: "",
      cueColor: "",
      cueClass: "bright",
      stimulusDifficulty: "low",
      stimulusConflict: [],
      carRule: [],
    },
  },
  {
    CSI: 100,
    correctKeycode: 6,
    errorT1keycode: 2,
    rewardPoints: 1,
    trialNumber: 2,
    block: 1,
    releaseMechanism: 1,
    timeout_ms: 3400,
    correctKeychar: "l",
    trialType: "TASK",
    stimulusFile: "https://backend2.bgbehavior.com/gs01_license_images/gs01_120px_R_90_6_10_450_set_0010.png",
    cueFile: "target_precue_number.svg",
    packageFile: "package_low.svg",
    package_appearance: 150,
    laneMapping: 0,
    agentFile: "footer_LaneLabels.svg",
    validation: {
      letterCode: 15,
      numberCode: 27,
      letterBrightness: 90,
      numberBrightness: 10,
      backgroundBrightness: 450,
      blockType: 1,
      taskSwitch: 1,
      letterOrder: 1,
      cueShape: "",
      cueColor: "",
      cueClass: "number",
      stimulusDifficulty: "high",
      stimulusConflict: [],
      carRule: [],
    },
  },
  {
    CSI: 100,
    correctKeycode: 3,
    errorT1keycode: 5,
    rewardPoints: 1,
    trialNumber: 3,
    block: 1,
    releaseMechanism: 1,
    timeout_ms: 3400,
    correctKeychar: "f",
    trialType: "TASK",
    stimulusFile: "https://backend2.bgbehavior.com/gs01_license_images/gs01_120px_K_90_7_10_450_set_0004.png",
    cueFile: "target_precue_letter.svg",
    packageFile: "package_low.svg",
    package_appearance: 150,
    laneMapping: 0,
    agentFile: "footer_LaneLabels.svg",
    validation: {
      letterCode: 14,
      numberCode: 25,
      letterBrightness: 90,
      numberBrightness: 10,
      backgroundBrightness: 450,
      blockType: 1,
      taskSwitch: 1,
      letterOrder: 1,
      cueShape: "",
      cueColor: "",
      cueClass: "letter",
      stimulusDifficulty: "low",
      stimulusConflict: [],
      carRule: [],
    },
  },
  {
    CSI: 100,
    correctKeycode: 7,
    errorT1keycode: 1,
    rewardPoints: 1,
    trialNumber: 4,
    block: 1,
    releaseMechanism: 1,
    timeout_ms: 3400,
    correctKeychar: ";",
    trialType: "TASK",
    stimulusFile: "https://backend2.bgbehavior.com/gs01_license_images/gs01_120px_G_10_5_10_550_set_0009.png",
    cueFile: "target_precue_brightness.svg",
    packageFile: "package_low.svg",
    package_appearance: 150,
    laneMapping: 0,
    agentFile: "footer_LaneLabels.svg",
    validation: {
      letterCode: 13,
      numberCode: 24,
      letterBrightness: 10,
      numberBrightness: 10,
      backgroundBrightness: 550,
      blockType: 1,
      taskSwitch: 1,
      letterOrder: 1,
      cueShape: "",
      cueColor: "",
      cueClass: "bright",
      stimulusDifficulty: "high",
      stimulusConflict: [],
      carRule: [],
    },
  },
  {
    CSI: 100,
    correctKeycode: 6,
    errorT1keycode: 2,
    rewardPoints: 1,
    trialNumber: 8,
    block: 2,
    releaseMechanism: 2,
    timeout_ms: 3400,
    correctKeychar: "l",
    trialType: "TASK",
    stimulusFile: "https://backend2.bgbehavior.com/gs01_license_images/gs01_120px_E_90_6_10_450_set_0005.png",
    cueFile: "target_precue_number.svg",
    packageFile: "package_low.svg",
    package_appearance: 150,
    laneMapping: 0,
    agentFile: "footer_LaneLabels.svg",
    validation: {
      letterCode: 17,
      numberCode: 27,
      letterBrightness: 90,
      numberBrightness: 10,
      backgroundBrightness: 450,
      blockType: 2,
      taskSwitch: 0,
      letterOrder: 1,
      cueShape: "",
      cueColor: "",
      cueClass: "number",
      stimulusDifficulty: "high",
      stimulusConflict: [],
      carRule: [],
    },
  },
  // Trial marking the end of the adaptive sequence (validation.blockType === 98,
  // per the real backend's convention - see mission1.example.json's own trailing
  // FEEDBACK trial for a precedent). Still a real, renderable trial - not a
  // synthetic marker - so it comes through mockRequestFellowTrials like any other.
  {
    trialNumber: 9,
    block: 3,
    trialType: "FEEDBACK",
    validation: {
      blockType: 98,
    },
  },
];

// Mission-3-shaped trials, grounded in a real trial dump (gs01_mission_3_task_39_vGF02_DemoShort.json).
// Bus is off only on the first entry (the resume-duplicate of the last static trial);
// every entry after has it on with a clearly varied onset/speed/image, so the adaptive
// loop's effect is obvious. First entry must be trialNumber 2 to match the resume cursor
// (same convention as MISSION_0_TRIAL_POOL).
const MISSION_3_TRIAL_POOL = [
  {
    CSI: 500,
    correctKeycode: 3,
    errorT1keycode: 5,
    rewardPoints: 7,
    trialNumber: 2,
    block: 1,
    releaseMechanism: 1,
    timeout_ms: 3400,
    correctKeychar: "f",
    trialType: "TASK",
    stimulusFile: "https://backend.bgbehavior.com/gs01_license_images/gs01_120px_G_10_7_90_450_set_0001.png",
    cueFile: "target_round_blue_sad.svg",
    packageFile: "package_3star.svg",
    package_appearance: 150,
    laneMapping: 0,
    agentFile: "footer_LaneLabels_M3.svg",
    escapism: 1,
    occurrenceFrontbus: 0,
    timeOnsetFrontbus_fixed: 500,
    timeOnset_escapePackage: 0,
    escapePackagePoints: 3,
    escapePackage: "escapePackage_1star.svg",
    bombEscapePackage: 0,
    bombTargetPackage: 0,
    speedEscapePackage: 4,
    speedFrontbus: 4,
    frontbusImage: "",
    distanceFrontbus: 50,
    laneFrontbus: 7,
    lossFraming: 0,
    validation: {
      letterCode: 13,
      numberCode: 25,
      letterBrightness: 10,
      numberBrightness: 90,
      backgroundBrightness: 450,
      blockType: 1,
      taskSwitch: 1,
      letterOrder: 1,
      cueShape: "",
      cueColor: "blue",
      cueClass: "number",
      stimulusDifficulty: "high",
      stimulusConflict: [],
      carRule: [],
    },
  },
  {
    CSI: 500,
    correctKeycode: 5,
    errorT1keycode: 3,
    rewardPoints: 3,
    trialNumber: 3,
    block: 2,
    releaseMechanism: 1,
    timeout_ms: 3400,
    correctKeychar: "k",
    trialType: "TASK",
    stimulusFile: "https://backend.bgbehavior.com/gs01_license_images/gs01_120px_U_90_3_10_450_set_0010.png",
    cueFile: "target_round_blue_sad.svg",
    packageFile: "package_1star.svg",
    package_appearance: 150,
    laneMapping: 0,
    agentFile: "footer_LaneLabels_M3.svg",
    escapism: 1,
    occurrenceFrontbus: 1,
    timeOnsetFrontbus_fixed: 500,
    timeOnset_escapePackage: 0,
    escapePackagePoints: 7,
    escapePackage: "escapePackage_3star.svg",
    bombEscapePackage: 0,
    bombTargetPackage: 0,
    speedEscapePackage: 4,
    speedFrontbus: 4,
    frontbusImage: "Frontbus_2.svg",
    distanceFrontbus: 50,
    laneFrontbus: 7,
    lossFraming: 0,
    validation: {
      letterCode: 21,
      numberCode: 22,
      letterBrightness: 90,
      numberBrightness: 10,
      backgroundBrightness: 450,
      blockType: 2,
      taskSwitch: 1,
      letterOrder: 1,
      cueShape: "",
      cueColor: "blue",
      cueClass: "letter",
      stimulusDifficulty: "high",
      stimulusConflict: [],
      carRule: [],
    },
  },
  {
    CSI: 500,
    correctKeycode: 6,
    errorT1keycode: 2,
    rewardPoints: 5,
    trialNumber: 4,
    block: 2,
    releaseMechanism: 1,
    timeout_ms: 3400,
    correctKeychar: "l",
    trialType: "TASK",
    stimulusFile: "https://backend.bgbehavior.com/gs01_license_images/gs01_120px_E_90_6_10_450_set_0005.png",
    cueFile: "target_round_yellow_happy.svg",
    packageFile: "package_2star.svg",
    package_appearance: 150,
    laneMapping: 0,
    agentFile: "footer_LaneLabels_M3.svg",
    // Big jump from the previous trial (onset 5x, speed 3.5x) - makes the adaptive
    // loop's effect obvious rather than a subtle tweak.
    escapism: 1,
    occurrenceFrontbus: 1,
    timeOnsetFrontbus_fixed: 2500,
    timeOnset_escapePackage: 0,
    escapePackagePoints: 5,
    escapePackage: "escapePackage_2star.svg",
    bombEscapePackage: 0,
    bombTargetPackage: 0,
    speedEscapePackage: 14,
    speedFrontbus: 14,
    frontbusImage: "Frontbus_4.svg",
    distanceFrontbus: 50,
    laneFrontbus: 7,
    lossFraming: 0,
    validation: {
      letterCode: 17,
      numberCode: 27,
      letterBrightness: 90,
      numberBrightness: 10,
      backgroundBrightness: 450,
      blockType: 2,
      taskSwitch: 0,
      letterOrder: 1,
      cueShape: "",
      cueColor: "yellow",
      cueClass: "number",
      stimulusDifficulty: "high",
      stimulusConflict: [],
      carRule: [],
    },
  },
  {
    CSI: 500,
    correctKeycode: 2,
    errorT1keycode: 6,
    rewardPoints: 4,
    trialNumber: 5,
    block: 2,
    releaseMechanism: 1,
    timeout_ms: 3400,
    correctKeychar: "d",
    trialType: "TASK",
    stimulusFile: "https://backend.bgbehavior.com/gs01_license_images/gs01_120px_K_90_7_10_450_set_0004.png",
    cueFile: "target_round_green_neutral.svg",
    packageFile: "package_1star.svg",
    package_appearance: 150,
    laneMapping: 0,
    agentFile: "footer_LaneLabels_M3.svg",
    // Still on, at a value between the previous two - keeps every trial after the
    // first one showing the bus, each with a distinct onset/speed/image.
    escapism: 1,
    occurrenceFrontbus: 1,
    timeOnsetFrontbus_fixed: 1200,
    timeOnset_escapePackage: 0,
    escapePackagePoints: 4,
    escapePackage: "escapePackage_1star.svg",
    bombEscapePackage: 0,
    bombTargetPackage: 0,
    speedEscapePackage: 8,
    speedFrontbus: 8,
    frontbusImage: "Frontbus_1.svg",
    distanceFrontbus: 50,
    laneFrontbus: 7,
    lossFraming: 0,
    validation: {
      letterCode: 14,
      numberCode: 25,
      letterBrightness: 10,
      numberBrightness: 90,
      backgroundBrightness: 450,
      blockType: 2,
      taskSwitch: 1,
      letterOrder: 1,
      cueShape: "",
      cueColor: "green",
      cueClass: "bright",
      stimulusDifficulty: "low",
      stimulusConflict: [],
      carRule: [],
    },
  },
  // Ends the sequence (validation.blockType === 98), same as MISSION_0_TRIAL_POOL's.
  {
    trialNumber: 6,
    block: 3,
    trialType: "FEEDBACK",
    validation: {
      blockType: 98,
    },
  },
];

const TRIAL_POOLS_BY_MISSION = {
  0: MISSION_0_TRIAL_POOL,
  3: MISSION_3_TRIAL_POOL,
};

/**
 * Simulates the backend building and returning the next block of adaptive trials.
 * Stateless: picks up wherever `payload.trialNumber` points into the pool selected
 * by `payload.missionNumber`, so repeated/out-of-order calls behave predictably.
 * @param {Object} payload Same shape as the real POST /fellow payload (participant_id, start_date, missionNumber, block, trialNumber, build_block, gf_version)
 * @returns {Promise<{timeline: Array}>} Resolves with a batch of trial configs, normally ending with the pool's trailing trial (validation.blockType === 98) once reached
 */
export async function mockRequestFellowTrials(payload) {
  await wait(MOCK_NETWORK_DELAY_MS);

  if (!payload) {
    throw new Error("mockRequestFellowTrials: missing payload");
  }

  const trialPool = TRIAL_POOLS_BY_MISSION[payload.missionNumber] ?? MISSION_0_TRIAL_POOL;
  const startTrialNumber = payload.trialNumber ?? trialPool[0]?.trialNumber ?? 1;
  const startIndex = trialPool.findIndex((trial) => trial.trialNumber === startTrialNumber);

  if (startIndex === -1) {
    // Defensive fallback only - e.g. a trialNumber requested past the pool's
    // own trailing blockType-98 entry, which should already end the sequence.
    return {
      timeline: [
        {
          trialNumber: startTrialNumber,
          block: payload.block,
          trialType: "FEEDBACK",
          validation: { blockType: 98 },
        },
      ],
    };
  }

  const timeline = trialPool.slice(startIndex, startIndex + BATCH_SIZE);
  return { timeline };
}
